import React, { cloneElement, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import usePosition from "./usePosition";
import "./Tooltip.scss";

// Fontaine M24 — Tooltip. Visual contract: Style Guide V2 `tooltip` (raised bubble, caption text, small arrow).
// Hover / focus with a short delay on pointer devices; long-press or tap-to-toggle on touch. The trigger is
// described by the tooltip (aria-describedby). Replaces react-tooltip for new code.

const SHOW_DELAY = 300;
const LONG_PRESS = 450;

/**
 * @param {ReactNode} content          the tip (short — a sentence at most)
 * @param {ReactElement} children      ONE focusable element (button, link, icon button)
 * @param {"top"|"bottom"|"left"|"right"} [side="top"]
 * @param {number} [delay=300]
 * @param {boolean} [disabled]
 * @param {string} [className]          extra class on the bubble (e.g. a wider rich card)
 */
export default function Tooltip({ content, children, side = "top", delay = SHOW_DELAY, disabled = false, className = "" }) {
  const id = useId();
  const tipId = `ft-tip-${id.replace(/:/g, "")}`;
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const timer = useRef(null);
  const press = useRef(null);
  const [open, setOpen] = useState(false);
  const { style, side: placed } = usePosition(anchorRef, tipRef, { open, side, align: "center", offset: 8 });

  const show = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), delay); };
  const hide = () => { clearTimeout(timer.current); setOpen(false); };
  useEffect(() => () => { clearTimeout(timer.current); clearTimeout(press.current); }, []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") hide(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (disabled || !content) return children;

  const child = React.Children.only(children);
  const trigger = cloneElement(child, {
    ref: (el) => {
      anchorRef.current = el;
      const r = child.ref;
      if (typeof r === "function") r(el);
      else if (r) r.current = el;
    },
    "aria-describedby": open ? tipId : child.props["aria-describedby"],
    onMouseEnter: (e) => { child.props.onMouseEnter?.(e); show(); },
    onMouseLeave: (e) => { child.props.onMouseLeave?.(e); hide(); },
    onFocus: (e) => { child.props.onFocus?.(e); show(); },
    onBlur: (e) => { child.props.onBlur?.(e); hide(); },
    // touch: long-press shows; a tap while open hides
    onTouchStart: (e) => { child.props.onTouchStart?.(e); press.current = setTimeout(() => setOpen(true), LONG_PRESS); },
    onTouchEnd: (e) => { child.props.onTouchEnd?.(e); clearTimeout(press.current); if (open) setTimeout(hide, 1200); },
    onTouchMove: (e) => { child.props.onTouchMove?.(e); clearTimeout(press.current); },
  });

  return (
    <>
      {trigger}
      {open && typeof document !== "undefined"
        ? createPortal(
            // eslint-disable-next-line no-restricted-syntax -- TODO(M02): runtime floating position
            <span role="tooltip" id={tipId} ref={tipRef} className={`ft-tip ft-tip--${placed} ${className}`.trim()} style={style}>
              {content}
              <span className="ft-tip__arrow" aria-hidden="true" />
            </span>,
            document.body
          )
        : null}
    </>
  );
}
